import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Language } from '@rbilabs/intl';
import { useIntl } from 'react-intl';

import { IDeliveryAddressLocation } from 'components/address-modal/save-delivery-address/types';
import { FloatingLabelInput } from 'components/floating-label-fields';
import { FloatingLabelPhoneInput } from 'components/floating-label-fields/floating-label-phone-input';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { PHONE_NUMBER_DUMMY } from 'utils/constants';
import {
  FormValidationState,
  getPhoneNumberErrorMessage,
  isPhoneNumberValid,
  requiredString,
} from 'utils/form';
import { IPlaceAddress } from 'utils/geolocation';
import { formatDistanceToNow } from 'utils/language/format-date';

import { FreeDeliveryProgressContainer } from '../free-delivery-progress';
import { RestaurantDetailsModal } from '../restaurant-details-modal';

import { getLocalizedDeliveryAddress } from './common';
import PhoneAddressInformationToggle from './phone-address-information-toggle';
import SaveAddressSelection from './save-address-selection';
import { DetailsBox, StyledFloatingLabelTextArea, Text } from './styled';
import { ICartDetailsProps } from './types';

const DeliveryDetailsForm: React.FC<Omit<ICartDetailsProps, 'serviceMode'>> = ({
  serverOrder,
  setAreDeliveryDetailsValid,
}) => {
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [addressLine2Error, setAddressLine2Error] = useState<boolean>(false);

  const enableFreeDeliveryCheckoutProgress = useFlag(
    LaunchDarklyFlag.ENABLE_FREE_DELIVERY_CHECKOUT_PROGRESS_BAR
  );
  const enableRequireAddress2 = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  );
  const enableNewSaveAddress = useFlag(LaunchDarklyFlag.ENABLE_SAVE_DELIVERY_ADDRESS);

  const { region } = useLocale();

  const {
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    updateShouldSaveDeliveryAddress,
    savedDeliveryAddress,
    setSavedDeliveryAddress,
  } = useOrderContext();

  const enableNumericPhoneValidation = useFlag(LaunchDarklyFlag.ENABLE_NUMERIC_PHONE_VALIDATION);
  const enableCheckoutModalFzInformation = useFlag(
    LaunchDarklyFlag.ENABLE_CHECKOUT_MODAL_FZ_INFORMATION
  );
  const enableOverrideDeliveryTime = useFlag(LaunchDarklyFlag.ENABLE_OVERRIDE_DELIVERY_TIME);

  const phoneRef = useRef<HTMLInputElement>(null);

  const maxDeliveryTime = serverOrder?.delivery?.dropoff.beforeTime || '';

  const ETA = useMemo(
    () => (maxDeliveryTime ? formatDistanceToNow(new Date(maxDeliveryTime), Language.en) : ''),
    [maxDeliveryTime]
  );

  const handleSetInstructions = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDeliveryInstructions(e.target.value);
  };

  const { formatMessage } = useIntl();
  const localizedDeliveryAddress = getLocalizedDeliveryAddress(deliveryAddress, formatMessage);

  useEffect(() => {
    const addressL2 = deliveryAddress.addressLine2;
    if (enableRequireAddress2 && addressL2?.length <= 0) {
      setAddressLine2Error(true);
    } else {
      setAddressLine2Error(false);
    }
  }, [deliveryAddress, enableRequireAddress2, setAddressLine2Error]);

  useEffect(() => {
    if (
      enableNewSaveAddress &&
      savedDeliveryAddress?.address?.phoneNumber &&
      savedDeliveryAddress?.address?.phoneNumber !== PHONE_NUMBER_DUMMY
    ) {
      setOrderPhoneNumber(savedDeliveryAddress.address.phoneNumber);
    }
  }, [enableNewSaveAddress, savedDeliveryAddress, setOrderPhoneNumber]);

  const handleOrderPhoneNumberChange = useCallback(
    async (newPhoneNumber: string) => {
      const { valid, error } = await isPhoneNumberValid({
        phoneNumber: newPhoneNumber,
        simpleValidation: enableNumericPhoneValidation,
        country: region,
      });

      if (valid) {
        setPhoneNumberError('');
      } else {
        setPhoneNumberError(getPhoneNumberErrorMessage(error, formatMessage));
      }

      setOrderPhoneNumber(newPhoneNumber);
      if (enableNewSaveAddress) {
        setSavedDeliveryAddress((prevState: IDeliveryAddressLocation) => ({
          address: {
            ...prevState?.address,
            phoneNumber: newPhoneNumber,
          },
          coordinates: prevState?.coordinates,
        }));
      }

      if (setAreDeliveryDetailsValid) {
        setAreDeliveryDetailsValid(valid);
      }
    },
    [
      enableNewSaveAddress,
      enableNumericPhoneValidation,
      formatMessage,
      region,
      setAreDeliveryDetailsValid,
      setOrderPhoneNumber,
      setSavedDeliveryAddress,
    ]
  );

  const handleUnitNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    // TODO: Remove after update to React17
    // https://reactjs.org/docs/legacy-event-pooling.html
    e.persist();
    updateShouldSaveDeliveryAddress(false);
    setDeliveryAddress((previousDeliveryAddress: IPlaceAddress) => ({
      ...previousDeliveryAddress,
      addressLine2: e.target.value,
    }));
    if (enableRequireAddress2) {
      setAddressLine2Error(!requiredString(e.target.value));
    }
  };

  return (
    <DetailsBox aria-labelledby="order-details-section-heading">
      {maxDeliveryTime && (
        <Text data-testid="delivery-eta">
          <span
            aria-label={
              enableOverrideDeliveryTime
                ? formatMessage({ id: 'etaFixed' })
                : formatMessage({ id: 'etaFull' })
            }
          >
            {enableOverrideDeliveryTime
              ? formatMessage({ id: 'etaFixed' })
              : formatMessage({ id: 'eta' })}
          </span>
          {enableOverrideDeliveryTime ? '' : ': ' + ETA}
        </Text>
      )}
      <Text data-private>{localizedDeliveryAddress}</Text>
      {enableCheckoutModalFzInformation && <RestaurantDetailsModal />}
      <FloatingLabelInput
        required={enableRequireAddress2}
        name="addressLine2"
        data-testid="delivery-address-line2"
        label={formatMessage({ id: 'unitNumberLabel' })}
        value={deliveryAddress?.addressLine2}
        onChange={handleUnitNumberChange}
        validation={
          addressLine2Error
            ? FormValidationState.INVALID
            : deliveryAddress?.addressLine2
            ? FormValidationState.VALID
            : FormValidationState.PRISTINE
        }
      />
      <StyledFloatingLabelTextArea
        name="deliveryInstructions"
        labelId="deliveryInstructions"
        label={formatMessage({ id: 'deliveryDetailsInstructions' })}
        rows={1}
        value={deliveryInstructions}
        onChange={handleSetInstructions}
        maxLength={512}
        date-private
        validation={FormValidationState.PRISTINE}
      />
      {enableNumericPhoneValidation ? (
        <FloatingLabelInput
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleOrderPhoneNumberChange(e.target.value.replace(/[^+0-9]/, ''));
          }}
          data-testid="delivery-phone-number"
          label={formatMessage({ id: 'phoneNumberRequired' })}
          value={orderPhoneNumber}
          validation={
            phoneNumberError || !orderPhoneNumber
              ? FormValidationState.INVALID
              : FormValidationState.VALID
          }
          message={phoneNumberError}
          ref={phoneRef}
        />
      ) : (
        <FloatingLabelPhoneInput
          hideCountrySelector
          data-testid="delivery-phone-number"
          label={formatMessage({ id: 'phoneNumberRequired' })}
          onChange={handleOrderPhoneNumberChange}
          value={orderPhoneNumber}
          placeholder={formatMessage({ id: 'phoneNumberPlaceholder' })}
          validation={
            phoneNumberError || !orderPhoneNumber
              ? FormValidationState.INVALID
              : FormValidationState.VALID
          }
          message={phoneNumberError}
        />
      )}
      <PhoneAddressInformationToggle />
      {!enableNewSaveAddress && <SaveAddressSelection />}
      {enableFreeDeliveryCheckoutProgress && <FreeDeliveryProgressContainer />}
    </DetailsBox>
  );
};

export default DeliveryDetailsForm;
